import React from 'react';

import { createPalette, usePaletteHandle } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    AtomLink,
    Button,
    DialogRoot,
    useDialogTriggerState,
    useScreenType,
} from '@gitbook/spine-react';
import { filterOutNullable } from '@gitbook/util-ts';

import {
    CreateSiteDialog,
    UserAvatar,
    useCreateInOwner,
    useCreateSpaceCommands,
} from '../../../Components';
import { useLoggedInUser } from '../../../state';
import { useHomeContext } from '../HomeContext';

/**
 * Header for the home screen.
 */
export function HomeHeader(): React.ReactElement {
    const { organization } = useHomeContext();
    const { id: organizationId } = organization;
    const { isMobile } = useScreenType();
    const currentUser = useLoggedInUser();
    const { createCollection } = useCreateInOwner({
        organization: organizationId,
    });
    const [{ newSpace }, createSpaceElements] = useCreateSpaceCommands({
        organization: organizationId,
    });

    const canCreateSiteContent = organization.permissions.createContent;
    const buttonRef = React.useRef<HTMLButtonElement>(null);
    const paletteHandle = usePaletteHandle(buttonRef);
    const userCanCreate = newSpace || createCollection;

    const siteCreationDialogTrigger = useDialogTriggerState();

    return (
        <div className="flex w-full items-center justify-end gap-4">
            {organization.permissions.admin ? (
                <Button
                    kind="secondary"
                    label="Invite"
                    leadingIcon={Icon.UserAdd}
                    iconOnly={isMobile}
                    href={{
                        overlay: {
                            type: 'org_invite',
                            organization: organizationId,
                        },
                    }}
                />
            ) : null}
            {userCanCreate ? (
                <>
                    <Button
                        testID="home.createPaletteButton"
                        kind="primary"
                        label="New"
                        leadingIcon={Icon.Plus}
                        iconOnly={isMobile}
                        ref={buttonRef}
                        onPress={() =>
                            paletteHandle.open(
                                createPalette({
                                    groups: [
                                        {
                                            key: 'owner',
                                            commands: [
                                                newSpace
                                                    ? {
                                                          ...newSpace,
                                                          subtitle: 'A space for your content',
                                                          testID: 'home.createSpaceButton',
                                                      }
                                                    : null,
                                                canCreateSiteContent
                                                    ? {
                                                          key: 'new_docs_site',
                                                          icon: Icon.Globe,
                                                          title: 'New docs site',
                                                          subtitle: 'Publish your documentation',
                                                          testID: 'home.createSiteButton',
                                                          trigger: () => {
                                                              siteCreationDialogTrigger.open();
                                                          },
                                                      }
                                                    : null,
                                            ].filter(filterOutNullable),
                                        },
                                    ],
                                })
                            )
                        }
                    />

                    {canCreateSiteContent ? (
                        <DialogRoot {...siteCreationDialogTrigger.rootProps}>
                            <CreateSiteDialog organizationId={organizationId} />
                        </DialogRoot>
                    ) : null}
                </>
            ) : null}
            {currentUser ? (
                <AtomLink
                    href={{
                        screen: {
                            type: 'account',
                            tab: 'profile',
                        },
                    }}
                    className="flex cursor-pointer"
                >
                    <UserAvatar user={currentUser} size="md" />
                </AtomLink>
            ) : null}
            {createSpaceElements}
        </div>
    );
}
